import { useState } from "react";
import {
  Lock,
  Sparkles,
  ArrowRight,
  ArrowUpRight,
  Check,
  Crown,
  Zap,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FEATURE_DEFINITIONS, PLAN_INFO } from "@/config/plans";
import { usePlanFeature, useFeatures } from "@/hooks/usePlanFeature";
import { cn } from "@/lib/utils";

/**
 * FeatureUnavailable Component
 * Shows an upgrade prompt when an API returns a feature-gated 403 error.
 * Matches the empty state design pattern used throughout the app.
 *
 * @param {object} error - Error object with { message, requiredPlan, upgradeUrl }
 * @param {React.ReactNode} icon - Optional icon component (defaults to Crown)
 * @param {string} description - Optional additional description text
 * @param {string} className - Optional className for the container
 */
export const FeatureUnavailable = ({
  error,
  icon: Icon = Crown,
  description,
  className,
}) => {
  const { t } = useTranslation();

  if (!error) return null;

  return (
    <Card className={className}>
      <CardContent className="p-0">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
            <Icon className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-1">
            {t("billing.upgradeRequired")}
          </h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-md px-4">
            {error.message}
          </p>
          {description && (
            <p className="text-sm text-muted-foreground mb-4 max-w-md px-4">
              {description}
            </p>
          )}
          <Link to={error.upgradeUrl || "/settings/billing"}>
            <Button className="gap-2">
              {t("billing.viewPlans")}
              <ArrowUpRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * UpgradePrompt Component
 * Shows a modal/card prompting user to upgrade for a specific feature
 *
 * @param {string} feature - Feature key that requires upgrade
 * @param {boolean} open - Whether the modal is open (for controlled mode)
 * @param {function} onOpenChange - Callback when modal open state changes
 * @param {string} variant - 'modal' | 'inline' | 'banner'
 */
export const UpgradePrompt = ({
  feature,
  open,
  onOpenChange,
  variant = "modal",
  title,
  description,
  className,
}) => {
  const navigate = useNavigate();
  const { plan: currentPlan } = useFeatures();

  const featureInfo = FEATURE_DEFINITIONS[feature];
  const { minimumPlan, minimumPlanName } = usePlanFeature(feature);
  const planInfo = PLAN_INFO[minimumPlan] || PLAN_INFO.starter;

  const handleUpgrade = () => {
    navigate("/billing");
    onOpenChange?.(false);
  };

  const FeatureIcon = featureInfo?.icon || Sparkles;

  // Inline variant - shows as a card within the page
  if (variant === "inline") {
    return (
      <Card
        className={cn(
          "border-dashed border-2 border-primary/30 bg-primary/5",
          className,
        )}
      >
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-full bg-primary/10 p-4 mb-4">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-lg font-semibold mb-2">
            {title || `Upgrade to ${minimumPlanName}`}
          </h3>
          <p className="text-muted-foreground mb-4 max-w-md">
            {description ||
              featureInfo?.description ||
              `This feature is available on the ${minimumPlanName} plan and above.`}
          </p>
          <div className="flex items-center gap-2 mb-6">
            <FeatureIcon className="h-5 w-5 text-primary" />
            <span className="font-medium">{featureInfo?.name}</span>
            <Badge variant="outline" className="ml-2">
              {minimumPlanName}
            </Badge>
          </div>
          <Button onClick={handleUpgrade} className="gap-2">
            <Zap className="h-4 w-4" />
            Upgrade Now
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Banner variant - shows as a banner at top of page
  if (variant === "banner") {
    return (
      <div
        className={cn(
          "bg-linear-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-4 mb-6",
          className,
        )}
      >
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-primary/20 p-2">
              <Lock className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-medium">
                {title ||
                  `${featureInfo?.name} requires ${minimumPlanName} plan`}
              </p>
              <p className="text-sm text-muted-foreground">
                {description || featureInfo?.description}
              </p>
            </div>
          </div>
          <Button onClick={handleUpgrade} size="sm" className="gap-2">
            <Zap className="h-4 w-4" />
            Upgrade to {minimumPlanName}
          </Button>
        </div>
      </div>
    );
  }

  // Modal variant (default)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="rounded-full bg-primary/10 p-3">
              <FeatureIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <DialogTitle>
                {featureInfo?.name || "Premium Feature"}
              </DialogTitle>
              <DialogDescription className="mt-1">
                {featureInfo?.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Badge variant="outline" className="text-muted-foreground">
              {PLAN_INFO[currentPlan]?.name || "Free"}
            </Badge>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <Badge className="bg-primary">{minimumPlanName}</Badge>
          </div>

          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-500" />
                  {planInfo.name} Plan
                </CardTitle>
                {planInfo.price !== null && (
                  <div className="text-right">
                    <span className="text-2xl font-bold">
                      ${planInfo.price}
                    </span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                )}
              </div>
              <CardDescription>{planInfo.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-green-500" />
                  <span className="font-medium">{featureInfo?.name}</span>
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-500" />
                  Everything in {PLAN_INFO[currentPlan]?.name || "Free"}
                </li>
                <li className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-green-500" />
                  Priority support
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} className="gap-2">
            <Zap className="h-4 w-4" />
            Upgrade Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * FeatureGate Component
 * Wraps content that requires a specific feature.
 * Shows upgrade prompt if user doesn't have access.
 *
 * @param {string} feature - Feature key required
 * @param {ReactNode} children - Content to show if feature is available
 * @param {ReactNode} fallback - Optional custom fallback (default: UpgradePrompt inline)
 * @param {string} fallbackVariant - 'inline' | 'banner' | 'hidden'
 */
export const FeatureGate = ({
  feature,
  children,
  fallback,
  fallbackVariant = "inline",
  title,
  description,
  className,
}) => {
  const { hasAccess, loading } = usePlanFeature(feature);

  // Show loading state briefly
  if (loading) {
    return null;
  }

  // User has access - show children
  if (hasAccess) {
    return children;
  }

  // User doesn't have access - show fallback or upgrade prompt
  if (fallback) {
    return fallback;
  }

  if (fallbackVariant === "hidden") {
    return null;
  }

  return (
    <UpgradePrompt
      feature={feature}
      variant={fallbackVariant}
      title={title}
      description={description}
      className={className}
    />
  );
};

/**
 * FeatureBadge Component
 * Shows a small badge indicating feature requires upgrade
 *
 * @param {string} feature - Feature key
 * @param {string} size - 'sm' | 'md'
 */
export const FeatureBadge = ({ feature, size = "sm", className }) => {
  const { hasAccess, minimumPlanName } = usePlanFeature(feature);

  if (hasAccess) {
    return null;
  }

  const sizeClasses = {
    sm: "text-[10px] px-1.5 py-0.5",
    md: "text-xs px-2 py-1",
  };

  return (
    <Badge
      variant="outline"
      className={cn(
        "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800",
        sizeClasses[size],
        className,
      )}
    >
      {minimumPlanName}
    </Badge>
  );
};

/**
 * LockedOverlay Component
 * Shows a locked overlay on top of content
 *
 * @param {string} feature - Feature key
 * @param {ReactNode} children - Content to overlay
 */
export const LockedOverlay = ({ feature, children, className }) => {
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { hasAccess, loading } = usePlanFeature(feature);
  const featureInfo = FEATURE_DEFINITIONS[feature];

  if (loading || hasAccess) {
    return children;
  }

  return (
    <div className={cn("relative", className)}>
      {/* Blurred content */}
      <div className="pointer-events-none select-none blur-sm opacity-50">
        {children}
      </div>

      {/* Overlay */}
      <div
        className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-[2px] cursor-pointer"
        onClick={() => setShowUpgrade(true)}
      >
        <div className="text-center p-6">
          <div className="rounded-full bg-muted p-3 mx-auto mb-3 w-fit">
            <Lock className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="font-medium mb-1">{featureInfo?.name}</p>
          <p className="text-sm text-muted-foreground mb-3">
            Click to learn more
          </p>
          <Button size="sm" variant="outline">
            <Zap className="h-4 w-4 mr-2" />
            Upgrade
          </Button>
        </div>
      </div>

      {/* Upgrade modal */}
      <UpgradePrompt
        feature={feature}
        open={showUpgrade}
        onOpenChange={setShowUpgrade}
      />
    </div>
  );
};

export default UpgradePrompt;
